/*
  MTi USA PWA behaviors.
  Loaded only when the "Enable PWA" theme setting is on (see snippets/pwa-head.liquid).

  Responsibilities:
  - Track PWA lifecycle in GA4 via GTM dataLayer pushes (events: pwa_install,
    pwa_session, pwa_install_prompt_response, pwa_banner_dismissed,
    pwa_ios_instructions_open). GTM must map these to GA4 tags — see PWA-README.md.
  - Custom install banner: Android/Chrome uses the captured beforeinstallprompt;
    iOS gets guided "Add to Home Screen" instructions (no install API on iOS).
  - Banner etiquette: mobile (coarse-pointer) viewports only, shown on every
    visit, 30-day snooze on dismiss, never shown inside the installed app.
  - Standalone extras: bottom-nav active state + live cart badge.
  - Optional service worker registration when a root-scope endpoint is configured
    (Theme settings → PWA → Advanced). Blank = no registration attempt.
*/
(function () {
  'use strict';

  var DISMISS_KEY = 'mti-pwa-banner-dismissed-at';
  var SESSION_KEY = 'mti-pwa-session-tracked';
  var DISMISS_DAYS = 30;

  window.dataLayer = window.dataLayer || [];

  function track(event, params) {
    try {
      window.dataLayer.push(Object.assign({ event: event }, params || {}));
    } catch (e) {
      /* analytics must never break the page */
    }
  }

  /* Even touching window.localStorage throws SecurityError when the user blocks
     site data — resolve each store once, behind its own guard. */
  function safeStore(name) {
    try {
      var store = window[name];
      store.getItem('mti-pwa-probe');
      return store;
    } catch (e) {
      return null;
    }
  }
  var LS = safeStore('localStorage');
  var SS = safeStore('sessionStorage');

  function storageGet(store, key) {
    try { return store ? store.getItem(key) : null; } catch (e) { return null; }
  }
  function storageSet(store, key, value) {
    try { if (store) store.setItem(key, value); } catch (e) { /* private mode */ }
  }

  var isStandalone =
    (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    window.navigator.standalone === true;
  var isIOS =
    /iPhone|iPad|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  function isMobileScreen() {
    return !!(window.matchMedia &&
      window.matchMedia('(max-width: 989px) and (pointer: coarse)').matches);
  }

  /* ---- installed-app session tracking + standalone styling hooks ---- */
  if (isStandalone) {
    document.documentElement.classList.add('pwa-standalone');
    if (!storageGet(SS, SESSION_KEY)) {
      storageSet(SS, SESSION_KEY, '1');
      track('pwa_session', { pwa_platform: isIOS ? 'ios' : 'android_or_desktop' });
    }
    /* Stamp the cart so any order placed INSIDE the installed app is flagged on
       the order in Shopify admin + exports — a hard signal that survives GA4/UTM
       attribution models. "_"-prefixed keys stay hidden from the customer.
       Once per tab; harmless on an empty cart. */
    if (!storageGet(SS, 'mti-pwa-cart-stamped')) {
      storageSet(SS, 'mti-pwa-cart-stamped', '1');
      try {
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: {
            _ordered_from: 'MTi home-screen app',
            _app_platform: isIOS ? 'ios' : 'android_or_desktop'
          } })
        }).catch(function () { /* offline / non-critical */ });
      } catch (e) { /* non-critical */ }
    }
  }

  window.addEventListener('appinstalled', function () {
    track('pwa_install');
  });

  function snoozeBanner() {
    storageSet(LS, DISMISS_KEY, String(Date.now()));
  }

  /* QA overrides (persist for the browser session):
     ?pwa-preview=1   force the install banner (skips visit/dismissal rules)
     ?pwa-preview=app simulate the installed app (bottom nav, slim header)
     ?pwa-preview=off clear either mode */
  var previewParam = (window.location.search.match(/[?&]pwa-preview=([^&]+)/) || [])[1] || '';
  if (previewParam === 'off') {
    try { SS && SS.removeItem('mti-pwa-preview'); } catch (e) {}
  } else if (previewParam) {
    storageSet(SS, 'mti-pwa-preview', previewParam);
  }
  var previewMode = storageGet(SS, 'mti-pwa-preview') || '';
  var isPreviewMode = previewMode === '1';
  if (previewMode === 'app' && !isStandalone) {
    isStandalone = true;
    document.documentElement.classList.add('pwa-standalone');
  }

  /* ---- in-app order stamping, line-item level ----
     B2B checkouts are "submitted for review" and become draft orders, and that
     step DROPS cart attributes — so the cart stamp above never reaches the order
     (found 2026-09-23: #D186/#1213, an in-app order, arrived with no attributes).
     Line-item properties survive draft → order, so tag every add-to-cart made in
     the installed app. "_"-prefixed = hidden in cart, drawer and order pages. */
  if (isStandalone) {
    var STAMP_KEY = '_ordered_from';
    var STAMP_VALUE = 'MTi home-screen app';
    var CART_ADD_RE = /\/cart\/add(\.js)?(\?|$)/;

    function stampJsonBody(body) {
      try {
        var data = JSON.parse(body);
        var items = data.items || [data];
        items.forEach(function (item) {
          item.properties = item.properties || {};
          item.properties[STAMP_KEY] = STAMP_VALUE;
        });
        return JSON.stringify(data);
      } catch (e) {
        return body;
      }
    }

    function stampBody(body) {
      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        body.set('properties[' + STAMP_KEY + ']', STAMP_VALUE);
        return body;
      }
      if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
        body.set('properties[' + STAMP_KEY + ']', STAMP_VALUE);
        return body;
      }
      if (typeof body === 'string') {
        return body.charAt(0) === '{'
          ? stampJsonBody(body)
          : body + (body ? '&' : '') + encodeURIComponent('properties[' + STAMP_KEY + ']') +
            '=' + encodeURIComponent(STAMP_VALUE);
      }
      return body;
    }

    /* AJAX adds — product form, quick order list, Buy again, anything else. */
    var pwaOrigFetch = window.fetch;
    if (pwaOrigFetch) {
      window.fetch = function (input, init) {
        try {
          var url = typeof input === 'string' ? input : (input && input.url) || '';
          if (init && init.body && CART_ADD_RE.test(url.split('#')[0])) {
            init = Object.assign({}, init, { body: stampBody(init.body) });
          }
        } catch (e) { /* never block an add-to-cart */ }
        return pwaOrigFetch.apply(this, [input, init]);
      };
    }

    /* Plain (non-AJAX) form posts to /cart/add. Capture phase runs before the
       product-form handler builds its FormData, so both paths see the input. */
    document.addEventListener('submit', function (evt) {
      var form = evt.target;
      if (!form || !form.getAttribute) return;
      if (!CART_ADD_RE.test(form.getAttribute('action') || '')) return;
      var name = 'properties[' + STAMP_KEY + ']';
      if (form.querySelector('input[name="' + name + '"]')) return;
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = STAMP_VALUE;
      form.appendChild(input);
    }, true);
  }

  /* Explicit "install now" intent from the launch email / how-to article
     (?pwa-install=1). The banner already shows to every mobile visitor; this
     just re-shows it to someone who previously dismissed it (skips the 30-day
     snooze). Stays mobile-only and never fires inside the installed app.
     Persists for the tab so in-session navigation keeps the intent. */
  var installParam = /[?&]pwa-install=1(?:&|$)/.test(window.location.search);
  if (installParam) storageSet(SS, 'mti-pwa-install-intent', '1');
  var installIntent = installParam || storageGet(SS, 'mti-pwa-install-intent') === '1';

  function bannerEligible() {
    if (isStandalone) return false;
    if (isPreviewMode) return true;
    if (!isMobileScreen()) return false;
    if (installIntent) return true;   // came from an explicit install link — skip the snooze too
    var dismissedAt = parseInt(storageGet(LS, DISMISS_KEY) || '0', 10);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_DAYS * 864e5) return false;
    return true;
  }

  /* ---- install banner ---- */
  var deferredPrompt = null;

  function initBanner() {
    var banner = document.getElementById('PwaInstallBanner');
    if (!banner) return;

    var installBtn = banner.querySelector('.pwa-install-banner__install');
    var dismissBtn = banner.querySelector('.pwa-install-banner__dismiss');
    var iosPanel = document.getElementById('PwaIosInstructions');
    var androidPanel = document.getElementById('PwaAndroidInstructions');

    function show(platform) {
      if (!bannerEligible()) return;
      banner.hidden = false;
      banner.dataset.platform = platform;
      if (installBtn) {
        if (platform === 'ios') installBtn.textContent = 'How to install';
        else if (platform === 'android') installBtn.textContent = deferredPrompt ? 'Install' : 'How to install';
      }
    }

    function hide() {
      banner.hidden = true;
    }

    window.addEventListener('beforeinstallprompt', function (e) {
      e.preventDefault();
      deferredPrompt = e;
      show('android');
    });

    // iOS never fires beforeinstallprompt; offer guided instructions instead.
    if (isIOS && !isStandalone) show('ios');

    // Arrived via an explicit install link (?pwa-install=1): surface guidance
    // immediately, before/without a beforeinstallprompt event.
    if (installIntent && !isStandalone) {
      if (isIOS) {
        show('ios');
        if (iosPanel) iosPanel.hidden = false;      // manual steps are the only iOS route
      } else if (!deferredPrompt) {
        show('android');                             // banner visible; steps panel is the fallback
      }
    }

    if (installBtn) {
      installBtn.addEventListener('click', function () {
        if (deferredPrompt) {
          deferredPrompt.prompt();
          deferredPrompt.userChoice.then(function (choice) {
            track('pwa_install_prompt_response', { pwa_choice: choice.outcome });
            // A spent prompt can't be reused this page load; snooze on dismissal
            // so the button doesn't turn into a dead end.
            if (choice.outcome === 'dismissed') snoozeBanner();
            hide();
            deferredPrompt = null;
          });
        } else if (isIOS && iosPanel) {
          var opening = iosPanel.hidden;
          iosPanel.hidden = !opening;
          if (opening) track('pwa_ios_instructions_open');
        } else if (androidPanel) {
          // Android, but Chrome hasn't offered its one-tap prompt yet — show manual steps.
          var openingA = androidPanel.hidden;
          androidPanel.hidden = !openingA;
          if (openingA) track('pwa_android_instructions_open');
        }
      });
    }

    if (dismissBtn) {
      dismissBtn.addEventListener('click', function () {
        snoozeBanner();
        installIntent = false;
        try { SS && SS.removeItem('mti-pwa-install-intent'); } catch (e) {}
        hide();
        track('pwa_banner_dismissed');
      });
    }
  }

  /* ---- bottom nav: live cart badge (active tab is server-rendered) ---- */
  function initBottomNav() {
    var nav = document.getElementById('PwaBottomNav');
    if (!nav) return;
    // Backup for the pre-paint head script (e.g. storage-restricted webviews).
    if (isStandalone) document.documentElement.classList.add('pwa-has-bottom-nav');

    // Keep the cart badge honest after AJAX add-to-cart (badge is server-rendered).
    var badge = nav.querySelector('.pwa-bottom-nav__badge');
    if (!badge || !isStandalone) return;

    function refreshBadge() {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cart) {
          if (!cart) return;
          badge.textContent = cart.item_count;
          badge.hidden = cart.item_count === 0;
        })
        .catch(function () { /* offline — keep last known count */ });
    }

    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, refreshBadge);
    }
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) refreshBadge();
    });
  }

  /* ---- optional service worker (root-scope endpoint required; see PWA-README.md) ---- */
  function initServiceWorker() {
    var configEl = document.getElementById('PwaConfig');
    if (!configEl || !('serviceWorker' in navigator)) return;
    var swPath = '';
    try { swPath = (JSON.parse(configEl.textContent).swPath || '').trim(); } catch (e) {}
    if (!swPath) return;
    navigator.serviceWorker.register(swPath).catch(function () {
      /* endpoint not live yet — silently skip */
    });
  }

  /* ---- app top-bar back button (injected into the header, wired to history) ---- */
  function initBackButton() {
    if (!isStandalone) return;
    var cluster = document.querySelector('.mti-v2-header .mti-v2-cluster');
    if (!cluster || cluster.querySelector('.mti-v2-pwa-back')) return;
    var search = cluster.querySelector('.mti-v2-search-trigger');
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'mti-v2-iconbtn mti-v2-pwa-back';
    btn.setAttribute('aria-label', 'Go back');
    btn.innerHTML =
      '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor"' +
      ' stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<path d="M15 18l-6-6 6-6"/></svg>';
    // Nothing to go back to on a fresh launch — hide until there's history.
    if (window.history.length <= 1) btn.hidden = true;
    btn.addEventListener('click', function () {
      if (window.history.length > 1) { window.history.back(); }
      else { window.location.href = '/'; }
    });
    if (search) { cluster.insertBefore(btn, search); }
    else { cluster.insertBefore(btn, cluster.firstChild); }
  }

  /* ---- in-app product-card "Add to cart" (single-variant cards) ---- */
  function initCardAddToCart() {
    if (!isStandalone) return;
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-app-add]');
      if (!btn) return;
      e.preventDefault();
      if (btn.getAttribute('data-busy')) return;
      btn.setAttribute('data-busy', '1');
      var id = btn.getAttribute('data-app-add');
      var label = btn.textContent;
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: id, quantity: 1 }] })
      }).then(function (r) { return r.ok ? r.json() : Promise.reject(); })
        .then(function () {
          btn.textContent = btn.getAttribute('data-app-added') || 'Added';
          if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
            try { window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'pwa-card-add' }); } catch (e) {}
          }
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          fetch('/cart.js', { headers: { Accept: 'application/json' } })
            .then(function (r) { return r.ok ? r.json() : null; })
            .then(function (cart) {
              if (!cart) return;
              var badge = document.querySelector('.pwa-bottom-nav__badge');
              if (badge) { badge.textContent = cart.item_count; badge.hidden = cart.item_count === 0; }
            }).catch(function () {});
          setTimeout(function () { btn.textContent = label; btn.removeAttribute('data-busy'); }, 1600);
        })
        .catch(function () {
          btn.textContent = 'Try again';
          setTimeout(function () { btn.textContent = label; btn.removeAttribute('data-busy'); }, 1600);
        });
    });
  }

  function init() {
    initBanner();
    initBottomNav();
    initBackButton();
    initCardAddToCart();
    initServiceWorker();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
